/**
 * Client (address book) synchronization for the web port.
 *
 * Mirrors `mobile/src/sync/syncClients.ts` byte-for-byte at the wire
 * level so a mobile peer accepts every blob this module emits and
 * vice-versa. The encrypted JSON payload uses the same `type:
 * 'client_upsert' | 'client_delete'` tags the mobile dispatcher
 * routes on; live blobs flow through the regular `sync` channel
 * (relay never decrypts them — see project CLAUDE.md).
 *
 * Wire shape (AES-CBC-256 over JSON, framed as `ivHex:base64(ct)`,
 * matching `web/src/sync/syncWork.ts` and the mobile pre-SEC-21
 * format that mobile's `decryptStringAuto` still accepts):
 *
 *   {
 *     type: 'client_upsert',
 *     client: { id, name, phone?, address?, taxId?, createdAt, updatedAt }
 *   }
 *
 *   { type: 'client_delete', clientId }
 *
 * The local persist + broadcast is intentionally split so callers
 * with optimistic UIs save first and the failed broadcast doesn't
 * tear the local row out from under the user — the relay client
 * already queues `_sendOrQueue` payloads and drains them on reconnect.
 */
import { aesCbcEncrypt, bytesToHex, hexToBytes, utf8ToBytes } from '../crypto/webCrypto';
import {
  saveClientLocal as dbSaveClient,
  deleteClientLocal as dbDeleteClient,
} from '../store/db';
import { relay } from './relay';
import type { WebIdentity } from '../store/keys';
import type { Client } from '../types';

interface ClientSyncMessage {
  type: 'client_upsert' | 'client_delete';
  client?: Client;
  clientId?: string;
}

async function encryptClientBlob(
  message: ClientSyncMessage,
  teamKeyHex: string,
): Promise<string> {
  const teamKey = hexToBytes(teamKeyHex);
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(16));
  const plaintext = utf8ToBytes(JSON.stringify(message));
  const ct = await aesCbcEncrypt(teamKey, plaintext, iv);
  let ctBin = '';
  for (let i = 0; i < ct.length; i++) ctBin += String.fromCharCode(ct[i]);
  return `${bytesToHex(iv)}:${btoa(ctBin)}`;
}

/**
 * Persist + broadcast a Client upsert.
 *
 * Always sets `updatedAt = Date.now()` before persistence so the
 * `applyClientUpsert` last-write-wins on every peer picks the newer
 * copy regardless of which device produced it.
 */
export async function broadcastClient(
  client: Client,
  identity: WebIdentity,
): Promise<Client> {
  const stamped: Client = { ...client, updatedAt: Date.now() };
  await dbSaveClient(stamped);
  try {
    const blob = await encryptClientBlob(
      { type: 'client_upsert', client: stamped },
      identity.teamKey,
    );
    relay.send(blob);
  } catch {
    // Encrypt/send failure must not pull the local row back — peer
    // devices will pick up the next outgoing broadcast (e.g. on the
    // following edit) and the relay's HTTP-poll fallback drains any
    // queued blobs after reconnect.
  }
  return stamped;
}

/** Persist + broadcast a Client delete. */
export async function broadcastClientDelete(
  clientId: string,
  identity: WebIdentity,
): Promise<void> {
  await dbDeleteClient(clientId);
  try {
    const blob = await encryptClientBlob(
      { type: 'client_delete', clientId },
      identity.teamKey,
    );
    relay.send(blob);
  } catch {
    // see comment in broadcastClient
  }
}

/** Test seam — exposed only so unit tests can verify the wire shape. */
export const __testing = { encryptClientBlob };
